import React, { Component } from "react"
import { Table, UncontrolledTooltip } from "reactstrap"
import gql from "graphql-tag"
import DatetimeRenderer from "../../Material/DatetimeRenderer"
import CarIssueModal from "../../Maintenance/CarIssueModal"
import ConfirmationPopup from "../../Material/ConfirmationPopup"
import withApolloClient from "../../withApolloClient"
import "./IncidentDamagesTable.css"

export const UpdateIncidentDamageMutation = gql`
    mutation UpdateIncidentDamageMutation($input: UpdateIncidentDamageMutationInput!) {
        updateIncidentDamage(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`

export const DAMAGE_COLUMNS = [
    { id: "date_addded", name: "Date Added", sortable: true },
    { id: "issueCategory", name: "Category", sortable: false },
    { id: "status", name: "Status", sortable: false },
    { id: "projectedCost", name: "Projected Cost", sortable: false },
    { id: "excludeFromCdw", name: "Exclude from CDW", sortable: false },
    { id: "severity", name: "Severity Level", sortable: false },
    { id: "canPickup", name: "Can Pickup", sortable: false },
    { id: "pictures", name: "Pictures", sortable: false },
    { id: "action", name: "Action", sortable: false },
]

const VARIANTS = {
    legacy: {
        CellTag: "small",
        tableClass: "incident-damages-table",
        cellClass: "",
        statusClass: "",
        severityClass: "",
        emptyStateClass: "incident-damages-empty--legacy",
        editButtonClass: "incident-damages-edit-icon",
    },
    overview: {
        CellTag: "div",
        tableClass: "incident-damages-table overview-soft-table overview-soft-table--damages",
        cellClass: "overview-inline-main",
        statusClass: "overview-status-pill",
        severityClass: "overview-severity-pill",
        emptyStateClass: "overview-empty-state",
        // The overview page originally styled this button with its own
        // color/hover treatment (see OverviewTab.css .overview-icon-button);
        // layer it on top of the shared reset so that styling survives here.
        editButtonClass: "incident-damages-edit-icon overview-icon-button",
    },
}

class IncidentDamagesTable extends Component {
    state = { openModal: null, cdwCandidate: null, cdwLoading: false, cdwError: null }

    toggleModal = (name) => {
        this.setState((prevState) => ({ openModal: prevState.openModal === name ? null : name }))
    }

    canEditCdw = (damage) => {
        if (this.props.hasPermissions?.includes("change_incidentdamage")) return true
        if (!damage.dateAdded) return true
        const ageInDays = (Date.now() - new Date(damage.dateAdded).getTime()) / (1000 * 60 * 60 * 24)
        // Server compares timedelta.days, which truncates; match it or the client
        // locks out damages aged 14-15 days that the server would accept.
        return Math.floor(ageInDays) <= 14
    }

    // Opening the confirmation captures the value we intend to write, so the
    // confirm handler cannot read a value that changed underneath it.
    openCdwConfirmation = (incidentDamageId, currentValue) => {
        this.setState({
            cdwCandidate: { id: incidentDamageId, nextValue: !currentValue },
            cdwError: null,
        })
    }

    closeCdwConfirmation = () => {
        this.setState({ cdwCandidate: null, cdwError: null, cdwLoading: false })
    }

    confirmExcludeFromCdw = () => {
        const candidate = this.state.cdwCandidate
        if (!candidate) return
        this.setState({ cdwLoading: true, cdwError: null })
        this.props.apolloClient
            .mutate({
                mutation: UpdateIncidentDamageMutation,
                variables: {
                    input: { incidentDamageId: candidate.id, excludeFromCdw: candidate.nextValue },
                },
            })
            .then((result) => {
                const payload = result?.data?.updateIncidentDamage
                if (payload && payload.ok) {
                    return Promise.resolve(this.props.refetchQuery()).finally(() =>
                        this.closeCdwConfirmation()
                    )
                }
                const message =
                    payload?.errors?.[0]?.messages?.toString() ||
                    "An error occurred, could not update CDW coverage."
                this.setState({ cdwError: message, cdwLoading: false })
            })
            .catch(() => {
                this.setState({ cdwError: "An error has occurred", cdwLoading: false })
            })
    }

    severityDescription = (severity) => {
        const levels = this.props.carIssueSeverityLevels?.enumValues || []
        return levels.find((item) => item.name === severity)?.description || "---"
    }

    renderPictures = (damage, variant) => {
        // The two variants nest this cell differently, so it is rendered whole
        // per variant rather than sharing a wrapper.
        const pictures = damage.pictures?.edges || []
        if (pictures.length === 0) {
            return variant === "overview" ? (
                <div className="overview-inline-subtle">No Pictures Found</div>
            ) : (
                <small>No Pictures Found</small>
            )
        }
        if (variant === "overview") {
            return (
                <div className="overview-picture-link" onClick={() => this.props.onViewPictures(pictures)}>
                    <i className="fa fa-file-image-o fa-lg" aria-hidden="true"></i>
                    <span>{pictures.length > 1 ? `+${pictures.length - 1} more` : "View"}</span>
                </div>
            )
        }
        return (
            <small>
                <div onClick={() => this.props.onViewPictures(pictures)}>
                    <i className="fa fa-file-image-o fa-lg" aria-hidden="true"></i>
                    {pictures.length > 1 && (
                        <a className="bos-custom-link">&nbsp;&nbsp;+{pictures.length - 1} more</a>
                    )}
                </div>
            </small>
        )
    }

    render() {
        const variant = this.props.variant || "legacy"
        const styles = VARIANTS[variant] || VARIANTS.legacy
        const { CellTag } = styles
        const damages = this.props.damages || []

        const candidate = this.state.cdwCandidate

        return (
            <>
            <Table responsive className={styles.tableClass}>
                <thead>
                    <tr>
                        <th>#</th>
                        {DAMAGE_COLUMNS.map((tableHeader) => (
                            <th key={tableHeader.id}>{tableHeader.name}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {damages.length > 0 ? (
                        damages.map((incidentDamage, i) => {
                            const damage = incidentDamage.node.damage
                            const isSelected = this.props.selectedDamages?.includes(incidentDamage.node.id)
                            const cdwDisabled = !this.canEditCdw(damage)
                            return (
                                <tr key={`incidentDamage__${i}`}>
                                    <td scope="row" className="incident-listing-checkbox-wrapper">
                                        {!isSelected && <span className="listing-counter">{i + 1}</span>}
                                        <span
                                            className="listing-checkbox"
                                            onClick={() => this.props.onToggleSelection(incidentDamage.node.id)}
                                        >
                                            {isSelected ? (
                                                <i className="fa fa-check-square-o" aria-hidden="true"></i>
                                            ) : (
                                                <i className="fa fa-square-o unchecked" aria-hidden="true"></i>
                                            )}
                                        </span>
                                    </td>
                                    <td>
                                        <CellTag className={styles.cellClass}>
                                            {damage.dateAdded ? (
                                                <DatetimeRenderer datetime={damage.dateAdded} placement="right" />
                                            ) : (
                                                "---"
                                            )}
                                        </CellTag>
                                    </td>
                                    <td>
                                        <CellTag className={styles.cellClass}>
                                            {damage && damage.issueCategory
                                                ? damage.issueCategory.name
                                                : "NO CATEGORY SELECTED"}
                                        </CellTag>
                                    </td>
                                    <td>
                                        <CellTag className={styles.statusClass}>
                                            {damage.status ? damage.status : "---"}
                                        </CellTag>
                                    </td>
                                    <td>
                                        <CellTag className={styles.cellClass}>
                                            {damage.projectedCost ? `$${damage.projectedCost}` : "---"}
                                        </CellTag>
                                    </td>
                                    <td>
                                        <span
                                            id={"excludeFromCdwWrapper" + i}
                                            tabIndex={cdwDisabled ? 0 : undefined}
                                            className={
                                                "incident-damages-cdw-control" +
                                                (cdwDisabled ? " incident-damages-cdw-control--gated" : "")
                                            }
                                        >
                                            <button
                                                type="button"
                                                className={
                                                    "incident-damages-cdw-pill" +
                                                    (incidentDamage.node.excludeFromCdw
                                                        ? " incident-damages-cdw-pill--excluded"
                                                        : " incident-damages-cdw-pill--covered")
                                                }
                                                aria-label={
                                                    "Change Exclude from CDW, currently " +
                                                    (incidentDamage.node.excludeFromCdw ? "True" : "False")
                                                }
                                                disabled={cdwDisabled}
                                                onClick={() =>
                                                    this.openCdwConfirmation(
                                                        incidentDamage.node.id,
                                                        !!incidentDamage.node.excludeFromCdw
                                                    )
                                                }
                                            >
                                                {incidentDamage.node.excludeFromCdw ? "True" : "False"}
                                                <i className="fa fa-pencil incident-damages-cdw-pill__icon" aria-hidden="true"></i>
                                            </button>
                                        </span>
                                        <UncontrolledTooltip target={"excludeFromCdwWrapper" + i}>
                                            {cdwDisabled
                                                ? "Cannot change CDW coverage on a damage older than 14 days"
                                                : "Click to change CDW coverage"}
                                        </UncontrolledTooltip>
                                    </td>
                                    <td>
                                        <CellTag className={styles.severityClass}>
                                            {this.severityDescription(damage.severity)}
                                        </CellTag>
                                    </td>
                                    <td>
                                        <CellTag className={styles.cellClass}>
                                            {damage.canPickup ? (
                                                <i className="fa fa-check"></i>
                                            ) : (
                                                <i className="fa fa-times"></i>
                                            )}
                                        </CellTag>
                                    </td>
                                    <td>{this.renderPictures(damage, variant)}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className={styles.editButtonClass}
                                            id={"editCarIssue" + i}
                                            onClick={() => this.setState({ openModal: "editCarIssue" + i })}
                                        >
                                            <i className="fa fa-pencil-square-o" aria-hidden="true"></i>
                                        </button>
                                        {this.state.openModal === "editCarIssue" + i && (
                                            <CarIssueModal
                                                open={this.state.openModal === "editCarIssue" + i}
                                                isUpdate
                                                carIssue={damage}
                                                handleClose={() => this.toggleModal("editCarIssue" + i)}
                                                refetchQuery={this.props.refetchQuery}
                                            />
                                        )}
                                        <UncontrolledTooltip target={"editCarIssue" + i}>
                                            Edit Car Issue
                                        </UncontrolledTooltip>
                                    </td>
                                </tr>
                            )
                        })
                    ) : (
                        <tr>
                            <td colSpan={DAMAGE_COLUMNS.length + 1}>
                                <div className={styles.emptyStateClass}>No Damages Found!</div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
            {candidate && (
                <ConfirmationPopup
                    open={!!candidate}
                    color="primary"
                    action="Confirm"
                    loading={this.state.cdwLoading}
                    error={this.state.cdwError}
                    handleClose={this.closeCdwConfirmation}
                    confirmAction={this.confirmExcludeFromCdw}
                    message={
                        candidate.nextValue
                            ? "Exclude this damage from CDW coverage? It will be charged to the driver in full instead of being capped by the deductible."
                            : "Return this damage to CDW coverage? It will be capped by the deductible again."
                    }
                />
            )}
            </>
        )
    }
}

export default withApolloClient(IncidentDamagesTable)
